import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, LinearProgress, Typography, useMediaQuery } from '@mui/material'
import { funnelValuesAt } from '../../pages/admin/useDemos'
import type { DemoFunnelStage } from '../../pages/admin/useDemos'

/** How long the count takes, unless the caller says otherwise. */
export const DEMO_FUNNEL_MS = 8000
const TICK_MS = 50

export interface DemoFunnelOverlayProps {
  /** The step's label ("Scan connected accounts"). */
  label: string
  /** The manifest's funnel: each stage's label and the figure it counts up to. */
  funnel: DemoFunnelStage[]
  durationMs?: number
  /** Called once, when the count has reached every stage's figure. */
  onAnimationEnd?: () => void
  /** The count is done but the step's data has not landed yet. */
  landing?: boolean
  /** Why the step failed; shown with Close. */
  error?: string | null
  onClose?: () => void
}

/**
 * The staged step's funnel (design/2026-10-22-chief-of-staff-and-demos.md
 * T20): labelled "Demo mode", the stages count up one after another
 * (3,500,000 posts scanned → 48,000 mention Acme → coded) while the server
 * lands the held-back data. The figures are the story's, not a
 * measurement. Under reduced motion they show at once.
 */
const DemoFunnelOverlay: React.FC<DemoFunnelOverlayProps> = ({
  label, funnel, durationMs = DEMO_FUNNEL_MS, onAnimationEnd, landing, error, onClose,
}) => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true })
  const [progress, setProgress] = useState(reducedMotion ? 1 : 0)
  const onEndRef = useRef(onAnimationEnd)
  onEndRef.current = onAnimationEnd

  // The count's timer, started on mount and cleared on unmount.
  useEffect(() => {
    if (reducedMotion) {
      onEndRef.current?.()
      return undefined
    }
    let elapsed = 0
    const id = setInterval(() => {
      elapsed += TICK_MS
      const p = Math.min(1, elapsed / durationMs)
      setProgress(p)
      if (p >= 1) {
        clearInterval(id)
        onEndRef.current?.()
      }
    }, TICK_MS)
    return () => clearInterval(id)
  }, [])

  const values = funnelValuesAt(funnel, progress)
  const top = Math.max(1, ...funnel.map((s) => s.value))
  const barWidth = (v: number) => (v <= 0 ? 0 : Math.max(2, (Math.log10(v + 1) / Math.log10(top + 1)) * 100))

  return (
    <Dialog open maxWidth="sm" fullWidth aria-labelledby="demo-funnel-title">
      <DialogContent sx={{ py: 4, px: 4 }}>
        <Chip label="Demo mode" color="info" size="small" sx={{ mb: 1.5, fontWeight: 700 }} />
        <Typography id="demo-funnel-title" variant="h5" sx={{ fontWeight: 700, mb: 3 }}>{label}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {funnel.map((stage, i) => (
            <Box key={`${stage.label}-${i}`}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{stage.label}</Typography>
                <Typography
                  data-testid={`funnel-value-${i}`}
                  variant="h4"
                  sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                >
                  {values[i].toLocaleString('en-GB')}
                </Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${barWidth(values[i])}%`, bgcolor: 'info.main', borderRadius: 3 }} />
              </Box>
            </Box>
          ))}
        </Box>
        {landing && !error && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Landing the data…</Typography>
            <LinearProgress color="info" />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
      </DialogContent>
      {error && (
        <DialogActions>
          <Button color="info" onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default DemoFunnelOverlay
